// Workout History Page

import { query } from '../db.js';
import { navigateTo } from '../router.js';

export async function renderHistoryPage() {
  const app = document.getElementById('app');

  const workouts = query(`
    SELECT wi.*,
           wt.name as template_name,
           wi.workout_type
    FROM PerformedWorkout wi
    LEFT JOIN Template wt ON wi.template_id = wt.id
    ORDER BY wi.date DESC, wi.id DESC
  `);

  app.innerHTML = `
    <div class="page safe-top">
      <div class="page-header">
        <h1 class="page-title">Workout History</h1>
        ${workouts.length > 0 ? `
          <p class="text-sm text-muted">${workouts.length} workout${workouts.length !== 1 ? 's' : ''} completed</p>
        ` : ''}
      </div>

      <div style="padding: 0 var(--spacing-md); margin-bottom: var(--spacing-xl);">
        ${workouts.length > 0 ? `
          <div style="display: flex; flex-direction: column; gap: var(--spacing-md);">
            ${workouts.map(workout => renderWorkoutCard(workout)).join('')}
          </div>
        ` : `
          <div class="empty-state">
            <svg class="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
            <h3 class="empty-state-title">No Workouts Yet</h3>
            <p class="empty-state-description">
              Complete your first workout to see it here
            </p>
            <button class="btn-primary" onclick="location.hash = '/templates'">
              Start a Workout
            </button>
          </div>
        `}
      </div>
    </div>
  `;

  // Event listeners for workout cards
  document.querySelectorAll('[data-workout-id]').forEach(card => {
    card.addEventListener('click', () => {
      const workoutId = card.getAttribute('data-workout-id');
      showWorkoutDetail(workoutId);
    });
  });
}

function renderWorkoutCard(workout) {
  if (workout.workout_type === 'cardio') {
    return renderCardioWorkoutCard(workout);
  }

  const sets = query(`
    SELECT COUNT(*) as count,
           SUM(CASE
             WHEN e.exercise_type IN ('reps_only', 'time_based') THEN 0
             ELSE COALESCE(ps.weight, 0) * COALESCE(ps.reps, 0)
           END) as volume
    FROM PerformedSets ps
    JOIN Exercises e ON ps.exercise_id = e.id
    WHERE ps.workout_instance_id = ?
  `, [workout.id])[0];

  const exercises = query(`
    SELECT DISTINCT e.name
    FROM PerformedSets ps
    JOIN Exercises e ON ps.exercise_id = e.id
    WHERE ps.workout_instance_id = ?
    ORDER BY ps.id
  `, [workout.id]);

  const duration = calculateDuration(workout.start_time, workout.end_time);
  const date = formatDate(workout.date);

  return `
    <div class="workout-card" data-workout-id="${workout.id}" style="border-left: 4px solid #6366F1;">
      <div class="workout-card-header">
        <div style="display: flex; align-items: center; gap: var(--spacing-sm);">
          <div style="width: 40px; height: 40px; background: linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%); border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3"></circle>
              <line x1="3" y1="12" x2="9" y2="12"></line>
              <line x1="15" y1="12" x2="21" y2="12"></line>
            </svg>
          </div>
          <div>
            <h3 class="workout-card-title">${workout.template_name}</h3>
            <p class="text-sm text-muted">${date}</p>
          </div>
        </div>
      </div>

      <div class="workout-card-meta" style="margin-top: var(--spacing-sm);">
        <span>${exercises.length} exercises</span>
        <span>${sets.count || 0} sets</span>
        ${duration ? `<span>${duration}</span>` : ''}
      </div>

      ${workout.studio ? `
        <p class="text-xs text-muted" style="margin-top: var(--spacing-sm);">
          📍 ${workout.studio}
        </p>
      ` : ''}
    </div>
  `;
}

function renderCardioWorkoutCard(workout) {
  const cardioData = query(`
    SELECT cd.*, ps.exercise_id, e.name as exercise_name, e.exercise_type
    FROM PerformedSets ps
    JOIN Exercises e ON ps.exercise_id = e.id
    LEFT JOIN CardioData cd ON cd.performed_set_id = ps.id
    WHERE ps.workout_instance_id = ?
  `, [workout.id])[0];

  if (!cardioData) return '';

  const date = formatDate(workout.date);

  // Jogging card
  if (cardioData.exercise_type === 'cardio_jogging') {
    const paceFormatted = formatPace(cardioData.pace_min_per_km);
    const durationFormatted = formatDurationMinutes(cardioData.duration_minutes);
    return `
      <div class="workout-card" data-workout-id="${workout.id}" style="border-left: 4px solid #FF9500;">
        <div class="workout-card-header">
          <div style="display: flex; align-items: center; gap: var(--spacing-sm);">
            <div style="width: 40px; height: 40px; background: linear-gradient(135deg, #FF9500 0%, #FFB647 100%); border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center;">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
              </svg>
            </div>
            <div>
              <h3 class="workout-card-title">${cardioData.exercise_name}</h3>
              <p class="text-sm text-muted">${date}</p>
            </div>
          </div>
        </div>
        <div class="workout-card-meta" style="margin-top: var(--spacing-sm);">
          <span>${cardioData.distance_km} km</span>
          <span>${paceFormatted} min/km</span>
          <span>${cardioData.speed_km_per_h} km/h</span>
          <span>${durationFormatted}</span>
        </div>
        ${workout.studio ? `<p class="text-xs text-muted" style="margin-top: var(--spacing-sm);">📍 ${workout.studio}</p>` : ''}
      </div>
    `;
  }

  // Sprint intervals card
  if (cardioData.exercise_type === 'cardio_intervals') {
    return `
      <div class="workout-card" data-workout-id="${workout.id}" style="border-left: 4px solid #FF9500;">
        <div class="workout-card-header">
          <div style="display: flex; align-items: center; gap: var(--spacing-sm);">
            <div style="width: 40px; height: 40px; background: linear-gradient(135deg, #FF9500 0%, #FFB647 100%); border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center;">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
              </svg>
            </div>
            <div>
              <h3 class="workout-card-title">${cardioData.exercise_name}</h3>
              <p class="text-sm text-muted">${date}</p>
            </div>
          </div>
        </div>
        <div class="workout-card-meta" style="margin-top: var(--spacing-sm);">
          <span>${cardioData.repetitions} intervals</span>
          <span>${cardioData.total_distance_km} km</span>
          <span>${cardioData.total_duration_minutes} min</span>
        </div>
        ${workout.studio ? `<p class="text-xs text-muted" style="margin-top: var(--spacing-sm);">📍 ${workout.studio}</p>` : ''}
      </div>
    `;
  }

  return '';
}

function showWorkoutDetail(workoutId) {
  const workout = query(`
    SELECT wi.*, wt.name as template_name, wi.workout_type
    FROM PerformedWorkout wi
    LEFT JOIN Template wt ON wi.template_id = wt.id
    WHERE wi.id = ?
  `, [workoutId])[0];

  if (!workout) return;

  if (workout.workout_type === 'cardio') {
    showCardioWorkoutDetail(workout);
    return;
  }

  const performedSets = query(`
    SELECT ps.*, e.name as exercise_name, e.musclegroup, e.exercise_type
    FROM PerformedSets ps
    JOIN Exercises e ON ps.exercise_id = e.id
    WHERE ps.workout_instance_id = ?
    ORDER BY ps.id
  `, [workoutId]);

  // Group by exercise
  const exerciseGroups = {};
  performedSets.forEach(set => {
    if (!exerciseGroups[set.exercise_id]) {
      exerciseGroups[set.exercise_id] = {
        name: set.exercise_name,
        musclegroup: set.musclegroup,
        sets: []
      };
    }
    exerciseGroups[set.exercise_id].sets.push(set);
  });

  const modal = document.createElement('div');
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal" style="max-width: 600px;">
      <div class="modal-header">
        <div>
          <h2 class="modal-title">${workout.template_name}</h2>
          <p class="text-sm text-muted">${formatDate(workout.date)}</p>
        </div>
        <button class="modal-close" id="close-detail-modal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div class="modal-body" style="max-height: 60vh; overflow-y: auto;">
        <!-- Summary -->
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--spacing-sm); margin-bottom: var(--spacing-lg); padding: var(--spacing-md); background: var(--color-surface); border-radius: var(--radius-lg);">
          <div style="text-align: center;">
            <p class="stat-value text-sm">${Object.keys(exerciseGroups).length}</p>
            <p class="stat-label">Exercises</p>
          </div>
          <div style="text-align: center;">
            <p class="stat-value text-sm">${performedSets.length}</p>
            <p class="stat-label">Sets</p>
          </div>
          <div style="text-align: center;">
            <p class="stat-value text-sm">${calculateDuration(workout.start_time, workout.end_time)}</p>
            <p class="stat-label">Duration</p>
          </div>
        </div>

        <!-- Exercises -->
        ${Object.values(exerciseGroups).map((exercise, idx) => `
          <div style="margin-bottom: var(--spacing-lg);">
            <div style="display: flex; align-items: center; gap: var(--spacing-md); margin-bottom: var(--spacing-sm);">
              <div class="exercise-number">${idx + 1}</div>
              <div>
                <h4 class="font-display">${exercise.name}</h4>
                <p class="text-xs text-muted" style="text-transform: capitalize;">${exercise.musclegroup}</p>
              </div>
            </div>

            ${exercise.sets.map((set, setIdx) => `
              <div style="display: flex; justify-content: space-between; padding: var(--spacing-sm); background: var(--color-surface); border-radius: var(--radius-md); margin-bottom: var(--spacing-xs);">
                <span class="text-sm text-muted">Set ${setIdx + 1}</span>
                <span class="stat-number text-sm">${formatSetDisplay(set.weight, set.reps, set.duration_seconds, set.exercise_type)}</span>
              </div>
            `).join('')}
          </div>
        `).join('')}

        ${workout.notes ? `
          <div style="padding: var(--spacing-md); background: var(--color-surface); border-radius: var(--radius-lg); margin-top: var(--spacing-lg);">
            <p class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Notes:</p>
            <p class="text-sm">${workout.notes}</p>
          </div>
        ` : ''}
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  modal.addEventListener('click', (e) => {
    if (e.target === modal || e.target.closest('#close-detail-modal')) {
      modal.remove();
    }
  });
}

function showCardioWorkoutDetail(workout) {
  const cardioData = query(`
    SELECT cd.*, ps.exercise_id, e.name as exercise_name, e.exercise_type
    FROM PerformedSets ps
    JOIN Exercises e ON ps.exercise_id = e.id
    LEFT JOIN CardioData cd ON cd.performed_set_id = ps.id
    WHERE ps.workout_instance_id = ?
  `, [workout.id])[0];

  const date = formatDate(workout.date);

  let detailsHTML = '';

  if (cardioData.exercise_type === 'cardio_jogging') {
    const paceFormatted = formatPace(cardioData.pace_min_per_km);
    const durationFormatted = formatDurationMinutes(cardioData.duration_minutes);
    detailsHTML = `
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-md); margin-top: var(--spacing-md);">
        <div style="text-align: center; padding: var(--spacing-md); background: var(--color-surface); border-radius: var(--radius-lg);">
          <p class="text-xs text-muted">Distance</p>
          <p class="stat-value">${cardioData.distance_km} km</p>
        </div>
        <div style="text-align: center; padding: var(--spacing-md); background: var(--color-surface); border-radius: var(--radius-lg);">
          <p class="text-xs text-muted">Duration</p>
          <p class="stat-value">${durationFormatted}</p>
        </div>
        <div style="text-align: center; padding: var(--spacing-md); background: var(--color-surface); border-radius: var(--radius-lg);">
          <p class="text-xs text-muted">Pace</p>
          <p class="stat-value">${paceFormatted} min/km</p>
        </div>
        <div style="text-align: center; padding: var(--spacing-md); background: var(--color-surface); border-radius: var(--radius-lg);">
          <p class="text-xs text-muted">Speed</p>
          <p class="stat-value">${cardioData.speed_km_per_h} km/h</p>
        </div>
      </div>
    `;
  } else if (cardioData.exercise_type === 'cardio_intervals') {
    detailsHTML = `
      <div style="margin-top: var(--spacing-md);">
        <div style="background: var(--color-surface); padding: var(--spacing-md); border-radius: var(--radius-lg); margin-bottom: var(--spacing-md);">
          <p class="text-xs text-muted" style="margin-bottom: var(--spacing-sm);">Interval Pattern</p>
          <p><strong>Sprint:</strong> ${cardioData.sprint_duration_sec}s @ ${cardioData.sprint_speed_kmh} km/h</p>
          <p><strong>Rest:</strong> ${cardioData.rest_duration_sec}s @ ${cardioData.rest_speed_kmh} km/h</p>
          <p><strong>Repetitions:</strong> ${cardioData.repetitions}x</p>
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-md);">
          <div style="text-align: center; padding: var(--spacing-md); background: var(--color-surface); border-radius: var(--radius-lg);">
            <p class="text-xs text-muted">Total Distance</p>
            <p class="stat-value">${cardioData.total_distance_km} km</p>
          </div>
          <div style="text-align: center; padding: var(--spacing-md); background: var(--color-surface); border-radius: var(--radius-lg);">
            <p class="text-xs text-muted">Total Duration</p>
            <p class="stat-value">${cardioData.total_duration_minutes} min</p>
          </div>
        </div>
      </div>
    `;
  }

  // Create modal
  const modal = document.createElement('div');
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal" style="max-width: 600px;">
      <div class="modal-header">
        <div>
          <h2 class="modal-title">${cardioData.exercise_name}</h2>
          <p class="text-sm text-muted">${date}</p>
          ${workout.studio ? `<p class="text-sm text-muted">📍 ${workout.studio}</p>` : ''}
        </div>
        <button class="modal-close" id="close-cardio-detail-modal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <div class="modal-body">
        ${detailsHTML}
        ${workout.notes ? `
          <div style="margin-top: var(--spacing-md); padding: var(--spacing-md); background: var(--color-surface); border-radius: var(--radius-lg);">
            <p class="text-xs text-muted" style="margin-bottom: var(--spacing-xs);">Notes</p>
            <p>${workout.notes}</p>
          </div>
        ` : ''}
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  modal.addEventListener('click', (e) => {
    if (e.target === modal || e.target.closest('#close-cardio-detail-modal')) {
      modal.remove();
    }
  });
}

function calculateDuration(startTime, endTime) {
  if (!startTime || !endTime) return null;

  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);

  const totalMinutes = (endH * 60 + endM) - (startH * 60 + startM);

  if (totalMinutes < 60) {
    return `${totalMinutes}m`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
}

function formatDate(dateStr) {
  // Return date in YYYY-MM-DD format
  return dateStr;
}

function formatVolume(volume) {
  if (volume >= 1000) {
    return `${(volume / 1000).toFixed(1)}k`;
  }
  return Math.round(volume);
}

// Helper functions for exercise sets display
function formatSetDisplay(weight, reps, duration, exerciseType) {
  if (exerciseType === 'time_based') {
    return formatSetDuration(duration);
  }

  if (!weight || weight === 0) {
    return `${reps} reps`;
  }

  return `${weight} kg × ${reps}`;
}

function formatSetDuration(seconds) {
  if (!seconds) return '0s';
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function formatPace(pace_minutes) {
  if (!pace_minutes) return '--';
  const minutes = Math.floor(pace_minutes);
  const seconds = Math.round((pace_minutes - minutes) * 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function formatDurationMinutes(duration_minutes) {
  if (!duration_minutes) return '--';
  const hours = Math.floor(duration_minutes / 60);
  const minutes = Math.floor(duration_minutes % 60);
  const seconds = Math.round((duration_minutes % 1) * 60);

  if (hours > 0) {
    if (minutes > 0 && seconds > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (seconds > 0) {
      return `${hours}h ${seconds}s`;
    } else {
      return `${hours}h`;
    }
  } else if (minutes > 0) {
    if (seconds > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${minutes}m`;
    }
  } else if (seconds > 0) {
    return `${seconds}s`;
  }
  return '0s';
}
